'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { EventParams } from '@/types/gtag';

export function useGoogleAnalytics(): void {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
    if (!measurementId) return;

    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      };
      
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false
      });
    }

    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', { 
      page_path: url,
      page_title: document.title,
      page_location: window.location.href
    });
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: EventParams = {}): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

export function trackAdSenseImpression(adSlot: string, page: string): void {
  trackEvent('adsense_impression', {
    ad_slot: adSlot,
    page_location: page
  });
}

export function trackAdSenseClick(adSlot: string, page: string): void {
  trackEvent('adsense_click', {
    ad_slot: adSlot,
    page_location: page,
    value: 1
  });
}

export function trackArticleView(articleTitle: string, category?: string): void {
  trackEvent('article_view', {
    article_title: articleTitle,
    article_category: category || 'general',
    engagement_time_msec: 100
  });
}

export function trackUserEngagement(action: string, label?: string, value?: number): void {
  trackEvent('user_engagement', {
    engagement_action: action,
    engagement_label: label,
    engagement_value: value
  });
}